/**
 * Encrypting and opening epoch-key envelopes (Storage Node Protocol v1 §5).
 *
 * Opening fails closed: a payload that is not an envelope, uses an unknown
 * algorithm or key scheme, names another context, or does not decrypt is an
 * error, never plaintext. A storage node or relay can withhold data, but it
 * cannot inject content the user never wrote.
 */
import {
  ENVELOPE_VERSION,
  ENVELOPE_VERSION_BOUND,
  decodeEnvelope,
  encodeEnvelope,
  isEnvelope,
  type EnvelopeHeader,
  type EnvelopeHeaderKind,
} from '@yada/storage-protocol';

/** What the ciphertext belongs to. Assets are content-addressed by the envelope hash, so they bind no object id. */
export interface EnvelopeContext {
  workspaceId: string;
  kind: EnvelopeHeaderKind;
  objectId?: string;
}

export type EpochKeyResolver = (epoch: number) => Promise<CryptoKey>;

/**
 * Version new envelopes are written with. Readers accept v1 and v2; writers move to
 * v2 only once a release in which every client (shell and CLI) reads v2 is deployed,
 * because older clients cannot open v2.
 */
export const ENVELOPE_WRITE_VERSION: number = ENVELOPE_VERSION;

export class EnvelopeIntegrityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EnvelopeIntegrityError';
  }
}

export interface SealOptions {
  epoch: number;
  key: CryptoKey;
  header?: Pick<EnvelopeHeader, 'mime_type' | 'doc_type' | 'content'>;
  version?: number;
}

export async function sealEpochEnvelope(
  plaintext: Uint8Array,
  context: EnvelopeContext,
  { epoch, key, header: extra = {}, version = ENVELOPE_WRITE_VERSION }: SealOptions
): Promise<Uint8Array> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const header: EnvelopeHeader = {
    kind: context.kind,
    alg: 'A256GCM',
    key: { scheme: 'epoch', epoch },
    iv: toBase64(iv),
    ...extra,
  };
  if (version === ENVELOPE_VERSION_BOUND) {
    header.ctx = { ws: context.workspaceId, ...(context.objectId !== undefined ? { id: context.objectId } : {}) };
  } else if (version !== ENVELOPE_VERSION) {
    throw new Error(`Cannot write envelope version ${version}`);
  }
  const prefix = encodeEnvelope(header, new Uint8Array(0), version);
  const params: AesGcmParams = { name: 'AES-GCM', iv: iv as unknown as BufferSource };
  if (version === ENVELOPE_VERSION_BOUND) params.additionalData = prefix as unknown as BufferSource;
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt(params, key, plaintext as unknown as BufferSource));
  const envelope = new Uint8Array(prefix.byteLength + ciphertext.byteLength);
  envelope.set(prefix, 0);
  envelope.set(ciphertext, prefix.byteLength);
  return envelope;
}

export async function openEpochEnvelope(
  bytes: Uint8Array,
  context: EnvelopeContext,
  resolveKey: EpochKeyResolver
): Promise<{ plaintext: Uint8Array; header: EnvelopeHeader }> {
  if (!isEnvelope(bytes)) {
    throw new EnvelopeIntegrityError(`Payload for ${describe(context)} is not an encrypted envelope`);
  }
  let decoded: ReturnType<typeof decodeEnvelope>;
  try {
    decoded = decodeEnvelope(bytes);
  } catch (err) {
    throw new EnvelopeIntegrityError(`Malformed envelope for ${describe(context)}: ${(err as Error).message}`);
  }
  const { header, version, ciphertext } = decoded;
  if (header.alg !== 'A256GCM' || header.key?.scheme !== 'epoch' || typeof header.key.epoch !== 'number') {
    throw new EnvelopeIntegrityError(`Unsupported envelope algorithm or key scheme for ${describe(context)}`);
  }

  const params: AesGcmParams = { name: 'AES-GCM', iv: fromBase64(header.iv) as unknown as BufferSource };
  if (version === ENVELOPE_VERSION_BOUND) {
    const ctx = header.ctx;
    if (
      header.kind !== context.kind ||
      !ctx ||
      ctx.ws !== context.workspaceId ||
      (context.objectId !== undefined && ctx.id !== context.objectId)
    ) {
      throw new EnvelopeIntegrityError(`Envelope belongs to another context than ${describe(context)}`);
    }
    params.additionalData = bytes.subarray(0, bytes.byteLength - ciphertext.byteLength) as unknown as BufferSource;
  }

  try {
    const key = await resolveKey(header.key.epoch);
    const plaintext = new Uint8Array(await crypto.subtle.decrypt(params, key, new Uint8Array(ciphertext) as unknown as BufferSource));
    return { plaintext, header };
  } catch {
    throw new EnvelopeIntegrityError(`Envelope for ${describe(context)} does not decrypt`);
  }
}

function describe(context: EnvelopeContext): string {
  return [context.kind, context.objectId, 'in workspace', context.workspaceId].filter(Boolean).join(' ');
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
